import React from "react";
import _ from "lodash";
import submit from "submit-form";

import Chart from "../../../../parts/chart";
import ProgramCard from "../../../../parts/cards/program-card";
import ToggleLinksInput from "../../../../parts/inputs/toggle-links-input";
import getCycleCardImagePath from "../../../../utils/get-cycle-card-image-path";
import generateAvatarSVG from "../../../../utils/generate-avatar-svg";

const {
  STRIPE,
  WEIGHT_UNITS,
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../../constants");

const handleLike = (e, cycle) => {
  submit(`/actions/cycle/like/${cycle._id}`, { method: "GET" });
};

function handleDayChange() {
  submit("#_", { body: document.getElementById("metrics-form") });
}

const ProgramOverview = (props, { locals }) => {
  const {
    editCycle,
    libraryCycles,
    activeProgramIndex,
    activeUser,
    organizationOwner,
    exercisesLiftsForCycle,
    exercisesVolumeForCycle,
    query,
  } = locals;
  const cycle = editCycle
    ? editCycle
    : _.find(libraryCycles, { _id: locals.params.cycleId });
  const { period, units } = query;

  const liftsTotals =
    Object.assign(
      {},
      ..._.map(exercisesLiftsForCycle, (k, i) => {
        return { [i]: _.sum(_.map(k)) };
      })
    ) || 0;
  const totalLiftSum = _.sum(_.map(_.omit(liftsTotals, "assistant")));
  let selectedMetrics =
    query.type === "total"
      ? exercisesLiftsForCycle
      : _.pick(exercisesLiftsForCycle, [query.type]);

  selectedMetrics = _.omit(selectedMetrics, "assistant");
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${PERIOD_FORMATS[period]}${i}`);
  const liftSeries = _.map(selectedMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });

  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(liftSeries, `data.${i}`))
  );
  const maxVolume = _.max(totalVolumes);

  // duplicate of the lift totals above to get total volume
  // TODO should try to not have to run this twice...maybe?
  const selectedVolumeMetrics =
    query.type === "total"
      ? exercisesVolumeForCycle
      : _.pick(exercisesVolumeForCycle, [query.type]);

  const volumeSeries = _.map(selectedVolumeMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });
  const accumulatedVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(volumeSeries, `data.${i}.volume`))
  );
  const [firstName, lastName] = _.get(cycle, "createdBy.name", "").split(/ +/);
  const image = _.get(cycle, "image.file", false)
    ? getCycleCardImagePath(cycle)
    : generateAvatarSVG(firstName, lastName);
  const program = cycle.programs[activeProgramIndex];

  const volumeTotalsByLift = Object.assign(
    {},
    ..._.map(exercisesVolumeForCycle, (exercise, i) => {
      return { [i]: _.sumBy(_.map(exercisesVolumeForCycle[i]), "volume") };
    })
  );

  const totalLiftVolume =
    _.get(volumeTotalsByLift, "bench", 0) +
    _.get(volumeTotalsByLift, "squat", 0) +
    _.get(volumeTotalsByLift, "deadlift", 0);

  const intensityTotals = Object.keys(selectedVolumeMetrics).reduce(
    (prev, exercise, it) => {
      let totals = prev;
      exercise = exercisesVolumeForCycle[exercise];

      const averages = _.map(Object.keys(exercise), (w, it) => {
        const week = _.get(exercise, `${w}`);

        if (week.peak > totals.peak) totals.peak = week.peak;
        const average = week.average === null ? 0 : week.average;
        totals.averages.push(average);

        if (!totals.peakSeries[w]) totals.peakSeries[w] = [];
        totals.peakSeries[w].push({ peak: week.peak, week: w });
        totals.averageSeries.push({
          average: average,
          lifts: week.lifts,
          week: w,
        });
      });
      totals.average = (_.sum(totals.averages) / totals.averages.length) * 100;

      return totals;
    },
    { averages: [], peak: 0, average: 0, peakSeries: [], averageSeries: [] }
  );

  const peakSeries = _(_.compact(intensityTotals.peakSeries))
    .map((week, it) => {
      return { x: it, y: _.max(_.map(week, "peak")) * 100 };
    })
    .flatten()
    .value();

  const averageSeries = _(_.compact(intensityTotals.averageSeries))
    .groupBy("week")
    .map((week, it) => {
      week.average = week.average * 100;
      return { x: it, y: (_.sum(_.map(week, "average")) / week.length) * 100 };
    })
    .flatten()
    .value();

  const minTick = 0; //Math.round(_.min(_.map(averageSeries, "y")) - 10)
  const maxTick = 100; //_.max(_.map(peakSeries, "y")) + 10;

  const selectedMetricsVolume =
    query.type === "total"
      ? exercisesVolumeForCycle
      : _.pick(exercisesVolumeForCycle, [query.type]);
  const selectedMetricsVolumeGroups = _.values(selectedMetricsVolume);

  // For a published cycle to be in My Programs, it has to be a duplicate
  const isMyProgram =
    program.status !== "published" &&
    (cycle.status !== "published" ||
      String(cycle.source) !== String(cycle._id));
  const isPublished = cycle.programs.every(
    (program) => program.status === "published"
  );

  let avatar = _.get(locals, "activeUser.avatar.file");
  avatar = avatar
    ? `/api/file/${avatar}?w=90&h=90`
    : firstName && lastName
    ? generateAvatarSVG(firstName, lastName)
    : "/img/avatars/avatar-placeholder.webp";

  const addToCalendarPath = `/actions/refresh?modal[type]=add-to-calendar&cycle=${
    cycle._id
  }&program=${_.get(program, "order")}`;
  const suggestUpgradePath = `/actions/refresh?modal[type]=suggest-upgrade`;
  const isCoach = _.get(activeUser, "permissions", []).indexOf("coach") > -1;
  const userPlan =
    (!activeUser.organizationId && !organizationOwner) || !isCoach
      ? _.get(activeUser, "stripe.plan", "")
      : _.get(organizationOwner, "stripe.plan", "");
  const isPremium =
    userPlan === STRIPE.PLANS.V2.premiumMonthly ||
    userPlan === STRIPE.PLANS.V2.premiumAnnually;
  const isAdmin = _.get(activeUser, "admin", false);
  const isAmbassador = activeUser.permissions.includes("ambassador");

  const athleteId = _.get(locals, "athlete._id", "");

  return (
    <div className="Program-Overview">
      <div className="Sub-Header">
        <div className="inset">
          {athleteId ? (
            <a href={`/athlete-portal/programs?athlete=${athleteId}`}>
              <div className="back">{`${
                _.get(locals, "athlete.name", "").split(/ +/)[0]
              }'s Programs`}</div>
            </a>
          ) : (
            <a href={"/coaching-portal/all-templates?owner=coach"}>
              <div className="back">{"Templates"}</div>
            </a>
          )}
          <div className="section-subheader">
            <div className="first-section-subheader">
              <div className="title">{cycle.name}</div>

              {isMyProgram && !isPublished && (
                <a
                  href={`/actions/refresh?modal[type]=edit-cycle-settings&cycleId=${cycle._id}`}
                  className="cycle-settings"
                >
                  Edit Cycle Settings
                </a>
              )}
            </div>
            <div className="second-section-subheader">
              <a
                href={`/actions/refresh?modal[type]=fetch&destinationCycleId=${cycle._id}&destinationCycleName=${cycle.name}&destinationCycleStatus=${cycle.status}`}
                className="fetch-program"
              >
                <i className="icon">
                  <svg width="22" height="22" viewBox="0 0 21.88 21.88">
                    <polygon points="7.16 12.49 7.16 13.84 13.84 13.84 13.84 7.15 12.49 7.15 12.49 11.54 4.58 3.63 3.63 4.58 11.53 12.49 7.16 12.49" />
                    <polygon points="9.45 3.43 9.45 4.78 17.1 4.78 17.1 17.1 4.78 17.1 4.78 9.45 3.43 9.45 3.43 18.45 18.45 18.45 18.45 3.43 9.45 3.43" />
                  </svg>
                </i>
                Fetch Program
              </a>
              {isMyProgram && (
                <a
                  href={`/actions/refresh?modal[type]=program-settings&cycleId=${cycle._id}`}
                  className="new-program"
                >
                  New Program
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="Main">
        <div className="title">
          {program ? program.name : "No programs found for this cycle"}
        </div>

        {program.status !== "pushed" && athleteId && (
          <a
            href={`/actions/refresh?modal[type]=confirm-push&cycle=${cycle._id}&program=${program.order}`}
            className="program-push"
          >
            <img
              src="/img/icons/icon_push-outline.svg"
              width="21"
              height="21"
            />{" "}
            Push
          </a>
        )}

        {Boolean(program) && program.status !== "pushed" && (
          <a
            href={`/${
              athleteId
                ? "athlete-portal/programs"
                : "coaching-portal/templates"
            }/edit-program/${cycle._id}?athlete=${athleteId}&program=${
              program.order
            }`}
            className="program-edit"
          >
            <img src="/img/icons/edit.svg" /> Edit
          </a>
        )}

        {Boolean(program) &&
          isMyProgram &&
          locals.query.referrer === "program-builder" && (
            <a
              href="/actions/refresh/?modal[type]=edit-program-settings"
              className="program-settings"
            >
              Settings
            </a>
          )}

        <a
          href={`/actions/refresh/?modal[type]=send&cycleId=${cycle._id}&programOrder=${program.order}`}
          className="program-duplicate"
        >
          Send
        </a>

        {/* <a href="#" className="program-delete"><img src="/img/icons/remove.svg"/> Delete</a> */}

        {Boolean(program) && (
          <div className="prev-next">
            <a
              href={`/actions/refresh/?program=${activeProgramIndex}`}
              className={`prev ${activeProgramIndex === 0 ? "disabled" : ""}`}
            />
            <span className="current">{activeProgramIndex + 1}</span>
            <span className="total">{cycle.programs.length}</span>
            <a
              href={`/actions/refresh/?program=${activeProgramIndex + 2}`}
              className={`next ${
                activeProgramIndex === cycle.programs.length - 1
                  ? "disabled"
                  : ""
              }`}
            />
          </div>
        )}

        {Boolean(program) && (
          <div>
            <div className="Program-Overview-Details">
              <div className="columns">
                <div className="column" data-col="1/4">
                  <div className="Chart-Frame program-data">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">Number of Lifts</div>
                      </div>
                    </div>
                    <div className="body">
                      <div className="chart">
                        <table>
                          <tbody>
                            <tr>
                              <td>Total</td>
                              <td>{totalLiftSum}</td>
                            </tr>
                            <tr className="bench">
                              <td>Bench</td>
                              <td>{liftsTotals.bench || 0}</td>
                            </tr>
                            <tr className="deadlift">
                              <td>Deadlift</td>
                              <td>{liftsTotals.deadlift || 0}</td>
                            </tr>
                            <tr className="squat">
                              <td>Squat</td>
                              <td>{liftsTotals.squat || 0}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column" data-col="1/4">
                  <div className="Chart-Frame program-data">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">Volume Split</div>
                      </div>
                    </div>
                    <div className="body">
                      <div className="chart">
                        <table>
                          <tbody>
                            <tr>
                              <td>Total</td>
                              <td>100%</td>
                            </tr>
                            <tr className="bench">
                              <td>Bench</td>
                              <td>
                                {Math.round(
                                  (volumeTotalsByLift.bench / totalLiftVolume) *
                                    100
                                ) || 0}
                                %
                              </td>
                            </tr>
                            <tr className="deadlift">
                              <td>Deadlift</td>
                              <td>
                                {Math.round(
                                  (volumeTotalsByLift.deadlift /
                                    totalLiftVolume) *
                                    100
                                ) || 0}
                                %
                              </td>
                            </tr>
                            <tr className="squat">
                              <td>Squat</td>
                              <td>
                                {Math.round(
                                  (volumeTotalsByLift.squat / totalLiftVolume) *
                                    100
                                ) || 0}
                                %
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column" data-col="1/4">
                  <div className="Chart-Frame program-data">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">Intensity</div>
                      </div>
                    </div>
                    <div className="body">
                      <div className="chart">
                        <table>
                          <tbody>
                            <tr>
                              <td>Average</td>
                              <td>
                                {intensityTotals.average === 0
                                  ? "N/A"
                                  : `${_.round(intensityTotals.average, 1)}%`}
                              </td>
                            </tr>
                            <tr>
                              <td>Peak</td>
                              <td>
                                {intensityTotals.average === 0
                                  ? "N/A"
                                  : `${_.round(
                                      intensityTotals.peak * 100,
                                      1
                                    )}%`}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column" data-col="1/4">
                  <div className="Chart-Frame program-data">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">Workouts</div>
                      </div>
                    </div>
                    <div className="body">
                      <div className="chart">
                        <table>
                          <tbody>
                            <tr>
                              <td>Total</td>
                              <td>{program.sessions.length}</td>
                            </tr>
                            <tr>
                              <td>Per Week</td>
                              <td>
                                {_(program.sessions)
                                  .groupBy("week")
                                  .map((w) => w.length)
                                  .max()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                {program.goals && (
                  <div className="column" data-col="1/4">
                    <div className="Chart-Frame program-data alt-text">
                      <div className="head">
                        <div className="left-side">
                          <div className="group">Goals</div>
                        </div>
                      </div>
                      <div className="body">
                        <div className="chart">{program.goals}</div>
                      </div>
                    </div>
                  </div>
                )}
                {program.equipmentRequired && (
                  <div className="column" data-col="1/4">
                    <div className="Chart-Frame program-data alt-text">
                      <div className="head">
                        <div className="left-side">
                          <div className="group">Equipment Required</div>
                        </div>
                      </div>
                      <div className="body">
                        <div className="chart">{program.equipmentRequired}</div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="column" data-col="1/2">
                  <div className="Chart-Frame program-data alt-text">
                    <div className="head">
                      <div className="left-side">
                        <div className="group">Program Description</div>
                      </div>
                    </div>
                    <div className="body">
                      <div className="chart">{program.description}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="Program-Overview-Chart">
              <form id="metrics-form" action="#_" method="GET" data-noreset>
                <div className="Chart-Frame bar-chart">
                  <div className="head">
                    <div className="right-side">
                      <div className="group">
                        <ToggleLinksInput
                          name="units"
                          options={[
                            { text: "kg", value: "metric" },
                            { text: "lbs", value: "imperial" },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="left-side">
                      <div className="group">
                        <ToggleLinksInput
                          className="tabs"
                          name="type"
                          options={[
                            { text: "Total", value: "total" },
                            { text: "Squat", value: "squat" },
                            { text: "Bench", value: "bench" },
                            { text: "Deadlift", value: "deadlift" },
                          ]}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="body">
                    {!_.isEmpty(selectedMetricsVolume) && (
                      <div>
                        <div className="legend">
                          {query.type !== "total" && (
                            <div className={`bar series ${query.type}`}>
                              Volume
                            </div>
                          )}
                          {intensityTotals.average > 0 && (
                            <div className="line series-a">Avg. Intensity</div>
                          )}
                          {intensityTotals.average > 0 && (
                            <div className="line series-b">Peak Intensity</div>
                          )}
                        </div>
                        <div className="axis-labels">
                          <div className="left">Lifts</div>
                          <div className="right">Intensity</div>
                        </div>
                        <div className="chart-fullsize">
                          <div className="labels">
                            <Chart
                              type="Bar"
                              only="label"
                              data={{ series: liftSeries }}
                              options={{
                                width: "60px",
                                height: "380px",
                                stackBars: true,
                                axisX: { showGrid: false, showLabel: false },
                                axisY: {
                                  showGrid: false,
                                  showLabel: true,
                                  onlyInteger: true,
                                  scaleMinSpace: 30,
                                },
                              }}
                            />

                            <Chart
                              type="Bar"
                              only="label"
                              data={{ series: [] }}
                              options={{
                                width: "60px",
                                height: "380px",
                                axisX: { showGrid: false, showLabel: false },
                                axisY: {
                                  type:
                                    process.browser &&
                                    require("chartist").StepAxis,
                                  stretch: true,
                                  showGrid: false,
                                  showLabel: true,
                                  ticks: _.range(minTick, maxTick, 10).map(
                                    (i) => `${i}%`
                                  ),
                                },
                              }}
                            />
                          </div>

                          <div className="data">
                            <Chart
                              type="Bar"
                              data={{
                                labels: labels,
                                series: liftSeries,
                              }}
                              options={{
                                width: `${_.size(keys) * 40}px`,
                                height: "380px",
                                stackBars: true,
                                chartPadding: { left: 35, right: 75 },
                                axisX: { showGrid: false, showLabel: true },
                                axisY: {
                                  showGrid: true,
                                  showLabel: false,
                                  scaleMinSpace: 30,
                                },
                              }}
                              tooltip={(item, i) => {
                                const key = keys[i];
                                return (
                                  <div
                                    className="tooltip"
                                    style={{ left: item.x }}
                                  >
                                    <div className="details">
                                      <div>
                                        <b>Week {key}</b>
                                      </div>
                                      {_.compact(
                                        _.map(liftSeries, ({ data, name }) => {
                                          const totalLifts = data[i];
                                          if (!totalLifts) return;
                                          return (
                                            <div key={name}>
                                              {_.capitalize(name)}{" "}
                                              <b>{totalLifts}</b>
                                            </div>
                                          );
                                        })
                                      )}
                                      <div>
                                        Total <b>{totalVolumes[i]}</b>
                                      </div>
                                      {/*<div>Volume <b>{locals.withWeight(locals.fromMetric(accumulatedVolumes[i], units), units)}</b></div>*/}
                                    </div>
                                  </div>
                                );
                              }}
                            />

                            {intensityTotals.average > 0 &&
                              _.size(keys) >= 2 && (
                                <Chart
                                  type="Line"
                                  data={{ series: [averageSeries, peakSeries] }}
                                  options={{
                                    width: `${_.size(keys) * 40}px`,
                                    height: "380px",
                                    showPoint: false,
                                    chartPadding: { left: 35, right: 75 },
                                    axisX: {
                                      showGrid: false,
                                      showLabel: false,
                                    },
                                    axisY: {
                                      low: minTick,
                                      high: maxTick, // / 100,
                                      showGrid: false,
                                      showLabel: false,
                                    },
                                  }}
                                />
                              )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="Program-Overview-Info">
              <div className="columns">
                <div className="column" data-col="2/3">
                  <h5>More information</h5>
                  <div className="small-text">
                    <p>{cycle.description}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {cycle.programs.length > 1 && (
          <div className="Program-Overview-More">
            <h5>More Programs in {cycle.name}</h5>
            <div className="columns">
              {_.map(_.sortBy(cycle.programs, "order"), (program, index) => (
                <ProgramCard
                  program={program}
                  cycle={cycle}
                  cardIndex={index}
                  isOverview={true}
                  referrer="program-library"
                  activeProgramIndex={activeProgramIndex}
                  athlete={athleteId}
                  isTemplate={!athleteId}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramOverview;
